import React, {ChangeEvent, FC, ReactElement, useCallback} from 'react';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import {makeStyles, SvgIcon} from '@material-ui/core';
import {useSearchContext, useSimpleSearchContext} from '@context';
import {useHistory} from 'react-router-dom';
import {Facet, SourceType} from '@types';
import {strings} from '@constants';
import propertyOf from 'lodash/propertyOf';
import sortBy from 'lodash/sortBy';
import {loadSvg} from '../icons/Icon';

const useStyles = makeStyles(theme => ({
    legend: {
        marginBottom: theme.spacing(2),
    },
    label: {
        color: theme.palette.text.primary,
        '&.Mui-focused': {
            color: theme.palette.text.primary,
        }
    },
    checkbox: {
        color: theme.palette.text.primary
    }
}));

const SOURCES_ORDER: Facet = {
    'community': 4,
    'documentation': 0,
    'partner_documentation': 2,
    'knowledge_base': 1,
    'release_notes': 3,
    'drafts': 5
};

const SourcePicker: FC = (): ReactElement => {
    const classes = useStyles();
    const history = useHistory();
    const {filter, updateFilter, result: {facets}} = useSearchContext();
    const {getSearchPath} = useSimpleSearchContext();

    const handleChange = useCallback(({target}: ChangeEvent<HTMLInputElement>): void => {
        const sources = target.checked
            ? [...filter.sources, target.name as SourceType]
            : filter.sources.filter(source => source !== target.name);

        updateFilter({sources});
        history.push({pathname: 'search', search: getSearchPath({src: sources})});
    }, [filter, updateFilter, history, getSearchPath]);


    return (
        <Box>
            <FormControl component="fieldset">
                <FormLabel
                    component="legend"
                    className={`${classes.legend} ${classes.label}`}>
                    {strings.resource}
                </FormLabel>
                <FormGroup>
                    {sortBy(SourceType.values, propertyOf(SOURCES_ORDER))
                        .map(source => (
                            <FormControlLabel
                                key={source}
                                label={`${strings.source(source)} (${facets[source]})`}
                                className={classes.label}
                                control={
                                    <Checkbox
                                        classes={{root: classes.checkbox}}
                                        color="default"
                                        name={source}
                                        checked={filter.sources.includes(source)}
                                        onChange={handleChange}
                                        // @ts-ignore
                                        icon={<SvgIcon component={loadSvg('square')}/>}
                                        size="small"/>
                                }/>
                        ))}
                </FormGroup>
            </FormControl>
        </Box>
    );
};

export default SourcePicker;
